import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './MidnightCategory.module.css';
import { formatPrice } from '../../utils/formatPrice';

const ROTATIONS = [
  styles.itemRotate1,
  styles.itemRotate2,
  styles.itemRotate3,
  styles.itemRotate4,
];

const SCRIBBLE_ROTATIONS = [
  '',
  styles.scribbleBoxRotate1,
  styles.scribbleBoxRotate2,
  styles.scribbleBoxRotate3,
];

const DIVIDER_STYLES = [
  { className: styles.divider },
  { className: `${styles.divider} ${styles.dividerRight}` },
  { className: `${styles.divider} ${styles.dividerShort}` },
  { className: styles.divider },
];

const BADGE_DEFS = [
  { key: 'isProductOfDay', altKey: 'productOfDay', emoji: '🌟', label: 'Día', className: 'itemBadgeProductOfDay' },
  { key: 'isSignature', altKey: 'signature', emoji: '✨', label: 'Signature', className: 'itemBadgeSignature' },
  { key: 'isRecommended', altKey: 'recommended', emoji: '⭐', label: 'Recomendado', className: 'itemBadgeRecommended' },
  { key: 'isMostOrdered', altKey: 'mostOrdered', emoji: '🔥', label: 'Más pedido', className: 'itemBadgeMostOrdered' },
  { key: 'isShareable', altKey: 'shareable', emoji: '👥', label: 'Compartir', className: 'itemBadgeShareable' },
];

const getItemBadges = (item) =>
  BADGE_DEFS.filter((def) => item[def.key] || item[def.altKey]);

const MidnightCategory = ({ category, showHeader = true }) => {
  const products = category?.products ?? [];
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Determine the appropriate archive subtext based on category
  const getArchiveSubtext = (categoryName) => {
    const name = (categoryName || '').toLowerCase();
    if (name.includes('tapa')) return 'ARCHIVE.bites';
    if (name.includes('frozen')) return 'ARCHIVE.iced';
    if (name.includes('mocktails')) return 'ARCHIVE.virgin';
    if (name.includes('vino') || name.includes('organico')) return 'ARCHIVE.pure';
    if (name.includes('bebida') || name.includes('zumo') || name.includes('cerveza')) return 'ARCHIVE.drinks';
    if (name.includes('cafeteria') || name.includes('café')) return 'ARCHIVE.brew';
    return 'ARCHIVE.dark';
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.noise} aria-hidden="true" />
      <div className={styles.lightStreaks}>
        <div className={`${styles.lightStreak} ${styles.lightStreakLeft}`} />
        <div className={`${styles.lightStreak} ${styles.lightStreakMid}`} />
        <div className={`${styles.lightStreak} ${styles.lightStreakRight}`} />
        <div className={styles.gradientTop} />
        <div className={styles.gradientBottom} />
      </div>
      <div className={`${styles.tapeMark} ${styles.tapeMarkTop}`} />
      <div className={`${styles.tapeMark} ${styles.tapeMarkMid}`} />
      <div className={`${styles.staple} ${styles.staple1}`} />
      <div className={`${styles.staple} ${styles.staple2}`} />

      <main className={`${styles.main} ${!showHeader ? styles.mainWithoutHeader : ''}`}>
        {showHeader && (
          <header className={styles.header}>
            <Link to="/Home" className={styles.backBtn} aria-label="Volver al menú">
              <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>
                close
              </span>
            </Link>
            <div className={styles.meta}>
              <p>BLISS Archive · Tapas</p>
              <p>Status: Restricted</p>
              <p>Scan #042-M</p>
            </div>
          </header>
        )}

        <div className={styles.titleBlock}>
          <h1 className={styles.title}>
            {category?.name?.toUpperCase() ?? 'TAPAS'}
            <br />
            <span className={styles.titleSub}>{getArchiveSubtext(category?.name)}</span>
          </h1>
          <div className={styles.badge}>ISSUE 02- Insolence servie</div>
        </div>

        <div className={styles.productList}>
          {products.map((item, i) => {
            const price = formatPrice(item.price);
            const rot = ROTATIONS[i % ROTATIONS.length];
            const scribble = SCRIBBLE_ROTATIONS[i % SCRIBBLE_ROTATIONS.length];
            const useAltBg = i % 4 === 2;
            const divStyle = DIVIDER_STYLES[i % DIVIDER_STYLES.length];
            const descClass = i % 4 === 2
              ? `${styles.itemDesc} ${styles.itemDescItalic}`
              : styles.itemDesc;
            const expanded = expandedIds.has(item.id);
            const hasDetails = !!item.description;
            const badges = getItemBadges(item);

            return (
              <div key={item.id} className={`${styles.item} ${rot}`}>
                {/* La fila es la misma abierta o cerrada: al abrir solo
                    aparece la descripcion debajo. Antes se cambiaba todo el
                    layout (titulo mas chico, precio saltando a la izquierda,
                    boton a todo el ancho) y la lista "saltaba" en el movil. */}
                <div
                  className={`${styles.itemInner} ${hasDetails ? styles.itemTappable : ''}`}
                  onClick={hasDetails ? () => toggleExpanded(item.id) : undefined}
                >
                  {badges.length > 0 && (
                    <div className={styles.itemBadges}>
                      {badges.map((badge) => (
                        <span key={badge.key} className={`${styles.itemBadge} ${styles[badge.className]}`}>
                          {badge.emoji} {badge.label}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className={styles.itemHeader}>
                    <h3 className={styles.itemTitle}>{item.name}</h3>
                    <div className={styles.itemHeaderRight}>
                      <div
                        className={`${styles.scribbleBox} ${scribble} ${useAltBg ? styles.scribbleBoxBg : ''}`}
                      >
                        <span className={styles.price}>{price}</span>
                      </div>
                    </div>
                  </div>
                  {expanded && (
                    <p id={`desc-${item.id}`} className={descClass}>{item.description}</p>
                  )}
                  {hasDetails && (
                    <button
                      type="button"
                      className={styles.seeMoreBtn}
                      onClick={(e) => {
                        e.stopPropagation(); // la fila entera ya alterna
                        toggleExpanded(item.id);
                      }}
                      aria-expanded={expanded}
                      aria-controls={`desc-${item.id}`}
                    >
                      {expanded ? 'Ver menos' : 'Ver más'}
                      <span className={styles.seeMoreSign} aria-hidden="true">{expanded ? '−' : '+'}</span>
                    </button>
                  )}
                </div>
                <div className={divStyle.className} />
              </div>
            );
          })}
        </div>

        <div className={styles.modBox}>
          <div className={`${styles.tapeMark} ${styles.tapeMarkBox}`} />
          <h4 className={styles.modLabel}>MODIFICATIONS [LOG]:</h4>
          <p className={styles.modText}>
          El caos es un arte, y nosotros lo abrazamos. Las imperfecciones son parte del viaje. Si no lo puedes manejar, este no es tu lugar.
            Calidad de archivo mantenida a alta presión. Lo amargo es intencional.
          </p>
          <div className={styles.modDots}>
            <div className={`${styles.modDot} ${styles.modDot1}`} />
            <div className={`${styles.modDot} ${styles.modDot2}`} />
            <div className={styles.modDot} />
          </div>
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerTop}>
            <div className={styles.footerLegal}>
              <p>DO NOT PHOTOCOPY</p>
              <p>© 2024 BLISS // MIDNIGHT DIV.</p>
              <p>ALL RIGHTS RESERVED IN DARKNESS</p>
            </div>
            <div className={styles.footerStamp}>
              <div className={styles.footerStampInner} />
            </div>
          </div>
          <div className={styles.footerRepeat}>
            <span className={styles.footerRepeatText}>
              midnightmidnightmidnightmidnightmidnightmidnightmidnightmidnightmidnightmidnight
            </span>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default MidnightCategory;
